import React, { FunctionComponent, useCallback } from "react";
import { ButtonGhostV2, colors2 } from "oolib";
import styled from "styled-components";
import { Lightbox, LightboxTrigger, LightboxContent, useLightbox } from "@/components/UI/lightbox";
import { ListItem as _ListItem } from "@/components/UI/List/comps/ListItem";
import { StyledListWrapper } from "@/components/UI/List";
import type { Placement } from "@floating-ui/react";

// This is done cuz ListItem is a jsx file which is being used in a tsx file, hence avoiding false typescript errors
const ListItem = _ListItem as any;

export interface ActionItem {
  display: string;
  icon?: string;
  onClick?: (e?: any) => void;
  disabled?: boolean;
}

export interface ActionMenuProps {
  icon?: string;
  actions: ActionItem[];
  placement?: Placement;
  style?: React.CSSProperties;
}

const StyledTriggerWrapper = styled.div<{ $isOpen: boolean }>`
  button {
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0.8)};
    &:hover {
      opacity: 1;
    }
  }
`;

const ActionMenuTrigger: FunctionComponent<{ icon: string }> = ({ icon }) => {
  const lightbox = useLightbox();
  return (
    <StyledTriggerWrapper $isOpen={!!lightbox?.open}>
      <ButtonGhostV2
        icon={icon}
        iconSize="S"
        S
        style={{
          padding: "0.4rem",
          ...(lightbox?.open && { backgroundColor: colors2.grey10 }),
        }}
      />
    </StyledTriggerWrapper>
  );
};

const ActionMenuContent: FunctionComponent<{ actions: ActionItem[] }> = ({ actions }) => {
  const lightbox = useLightbox();

  const handleChange = useCallback((option: any, e?: any) => {
    const action = actions.find((a) => a.display === option.value);
    if (action?.onClick) {
      action.onClick(e);
    }
    lightbox?.setOpen(false);
  }, [actions, lightbox]);

  const scrollNoop = useCallback(() => {}, []);

  return (
    <StyledListWrapper disableShadow>
      {actions.map((action, index) => (
        <ListItem
          key={action.display}
          option={{
            value: action.display,
            display: action.display,
            icon: action.icon,
            disabled: action.disabled,
          }}
          index={index}
          S
          isSelected={false}
          isFocussed={false}
          scrollFocussedOpIntoView={scrollNoop}
          onChange={handleChange}
        />
      ))}
    </StyledListWrapper>
  );
};

export const ActionMenu: FunctionComponent<ActionMenuProps> = ({
  icon = "DotsThree",
  actions,
  placement = "bottom-end",
  style,
}) => {
  if (!actions || actions.length === 0) return null;

  return (
    <div onClick={(e) => e.stopPropagation()}>
    <Lightbox placement={placement}>
      <LightboxTrigger>
        <ActionMenuTrigger icon={icon} />
      </LightboxTrigger>
      <LightboxContent style={{ zIndex: 10001, ...style }}>
        <ActionMenuContent actions={actions} />
      </LightboxContent>
    </Lightbox>
    </div>
  );
};
